/**
 * Number Field Type
 *
 * Provides configuration and renderers for number fields:
 * - Read-only column renderer
 * - Editable column renderer
 * - Form field renderer (with optional increment/decrement arrows)
 */

import { useCallback } from 'react';
import type { ColumnDef, CellContext } from '@tanstack/react-table';
import { NumberInput } from 'components';
import { Text } from 'components/text';
import { CellInput } from 'features/misc/table';
import { SharedEditableCell, useEditableContextOptional } from '../../components';
import { CellContainer } from '../../styles';
import { toast } from 'react-toastify';
import { t } from '@lingui/macro';
import type {
  FieldTypeConfig,
  FormFieldProps,
  ColumnRenderer,
  RenderOptions,
  CustomColumn,
} from '../../types';
import { clearEditOnSuccess } from '../inlineEditUtils';
import { validateFieldValue } from '../../validation';
import { FieldError, shouldShowFieldError } from './fieldError';
import { S } from './columnWidths';
import { CaretUp, CaretDown } from '@phosphor-icons/react';
import { InputAdornment, IconButton, Box } from '@mui/material';
import { smIconButton } from 'components/button/IconButton';
import { ClearFieldButton, ClearFieldFocusContainer } from '../../components/ClearFieldButton';
import { ClearedFieldIndicator } from '../../components/ClearedFieldIndicator';
import { getBulkEditClearState } from '../../hooks/useBulkEditClear';

// ============================================================================
// Editable Column Renderer
// ============================================================================

// Import shared cell value utility
import { getCellStringValue } from './cellValue';

// ============================================================================
// Read-Only Column Renderer
// ============================================================================

/**
 * Number read-only renderer - displays number in a cell container
 * Uses getValue() to support nested accessors
 */
export const numberReadOnlyRenderer = <T extends { id: number | string }>(
  _col: CustomColumn<T>,
  _options: RenderOptions<T>
): Partial<ColumnDef<T>> => ({
  cell: (cellContext: CellContext<T, unknown>) => {
    const value = cellContext.getValue();
    return (
      <CellContainer>
        <Text variant="bodyXs">{value != null ? String(value) : ''}</Text>
      </CellContainer>
    );
  },
  tooltipRender: (cell: CellContext<T, unknown>) => {
    const value = cell.getValue();
    return value != null ? String(value) : '';
  },
});

// ============================================================================
// Form Field Renderer
// ============================================================================

/**
 * Render a number input field for forms
 * Shows increment/decrement arrows by default (can be hidden with numberConfig.showArrows: false)
 */
export const renderNumberField = ({
  col,
  value,
  valueForValidation,
  onChange,
  isFirst,
  disabled,
  inputRef,
  numberConfig,
  bulkEditContext,
  validationContext,
}: FormFieldProps): React.ReactNode => {
  // Parse current value for arrow operations (default to 0 only for increment/decrement)
  const numericValue =
    typeof value === 'number'
      ? value
      : value !== '' && value != null
        ? parseFloat(value as string)
        : 0;
  const currentValue = isNaN(numericValue) ? 0 : numericValue;
  const step = numberConfig?.step ?? 1;
  const min = numberConfig?.min ?? (col as { min?: number }).min;
  const max = numberConfig?.max ?? (col as { max?: number }).max;
  const minExclusive = (col as { minExclusive?: boolean }).minExclusive === true;

  const handleIncrement = () => {
    const newValue = currentValue + step;
    if (max === undefined || newValue <= max) {
      onChange(col.key, newValue);
      validationContext?.onFieldTouched?.(col.key);
    }
  };

  const handleDecrement = () => {
    const newValue = currentValue - step;
    const minOk = min === undefined || (minExclusive ? newValue > min : newValue >= min);
    if (minOk) {
      onChange(col.key, newValue);
      validationContext?.onFieldTouched?.(col.key);
    }
  };

  const emptyDisplayNode = (col as { emptyDisplayNode?: React.ReactNode }).emptyDisplayNode;
  const valueEmpty = value == null || value === '';
  const showEmptyChip = valueEmpty && emptyDisplayNode;

  const endAdornment =
    numberConfig?.showArrows !== false ? (
      <InputAdornment position="end">
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <IconButton
            onClick={handleIncrement}
            disabled={disabled || (max !== undefined && currentValue >= max)}
            sx={{ ...smIconButton, marginBottom: '-4px', '&.Mui-disabled': { opacity: 0.3 } }}
          >
            <CaretUp size={14} />
          </IconButton>
          <IconButton
            onClick={handleDecrement}
            disabled={
              disabled ||
              (min !== undefined &&
                (minExclusive ? currentValue - step <= min : currentValue <= min))
            }
            sx={{ ...smIconButton, marginTop: '-4px', '&.Mui-disabled': { opacity: 0.3 } }}
          >
            <CaretDown size={14} />
          </IconButton>
        </Box>
      </InputAdornment>
    ) : undefined;

  // Use Zod validation to get error message (valueForValidation for bulk-edit "keep current")
  const valueToValidate = valueForValidation !== undefined ? valueForValidation : value;
  const validation = validateFieldValue(
    {
      ...col,
      type: 'number',
      ...(min !== undefined && { min }),
      ...(max !== undefined && { max }),
    },
    valueToValidate
  );
  const errorMessage = !validation.success ? validation.error : undefined;
  const showError = shouldShowFieldError(col.key, validationContext);

  const {
    isBulkEdit,
    isCleared,
    showClearButton,
    handleBulkEditChange,
    handleClear,
    handleUndoClear,
  } = getBulkEditClearState({
    value,
    valueForValidation,
    valueEmpty,
    colKey: col.key,
    required: (col as { required?: boolean }).required,
    onChange,
    bulkEditContext,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onChange(col.key, handleBulkEditChange(e.target.value));
    validationContext?.onFieldTouched?.(col.key);
  };

  if (isBulkEdit && isCleared) {
    return (
      <Box key={col.key}>
        <ClearedFieldIndicator fieldName={col.label} onClick={handleUndoClear} />
      </Box>
    );
  }

  return (
    <ClearFieldFocusContainer key={col.key}>
      <NumberInput
        label={col.label}
        placeholder={showEmptyChip ? '' : col.placeholder}
        fullWidth
        disabled={disabled}
        ref={isFirst ? inputRef : undefined}
        autoFocus={isFirst}
        value={value != null ? String(value) : ''}
        onChange={handleChange}
        labelSeparate
        variant="outlined"
        allowSignedValues
        InputProps={{
          ...(showEmptyChip ? { startAdornment: emptyDisplayNode } : {}),
          endAdornment,
        }}
      />
      <ClearFieldButton onClick={handleClear} visible={showClearButton} />
      <FieldError error={errorMessage} show={showError} />
    </ClearFieldFocusContainer>
  );
};

/**
 * Number editable renderer - unified pattern
 * Uses col.id as the data field, auto-infers update
 * Supports conditional editability via editable: (cell) => boolean
 */
export const numberEditableRenderer = <T extends { id: number | string }>(
  col: CustomColumn<T>,
  _options: RenderOptions<T>
): Partial<ColumnDef<T>> => {
  const editableProp = col.editable;

  return {
    cell: (context: CellContext<T, unknown>) => {
      const value = getCellStringValue(context, col.id);
      const isRowEditable =
        typeof editableProp === 'function' ? editableProp(context) : editableProp === true;

      if (!isRowEditable) {
        return (
          <CellContainer>
            <Text variant="bodyXs">{value}</Text>
          </CellContainer>
        );
      }

      return <NumberEditableCell context={context} col={col} value={value} />;
    },
    meta: {
      editable: true,
    },
    tooltipRender: (cell: CellContext<T, unknown>) => {
      const value = cell.getValue();
      return value != null ? String(value) : '';
    },
  };
};

const NumberEditableCell = <T extends { id: number | string }>({
  context,
  col,
  value,
}: {
  context: CellContext<T, unknown>;
  col: CustomColumn<T>;
  value: string;
}) => {
  const editableCtx = useEditableContextOptional();
  const clearEdit = useCallback(
    () => editableCtx?.setEditableState?.({ rowIndex: null, columnId: null }),
    [editableCtx?.setEditableState]
  );

  return (
    <SharedEditableCell
      {...context}
      meta={{ editable: true }}
      tooltipTitle={value}
      readModeEl={<span>{value}</span>}
    >
      <CellInput
        value={value || ''}
        type="number"
        onChange={newValue => {
          if (col.onUpdate) {
            const numValue = newValue === '' ? null : Number(newValue);
            if (
              (numValue === null || numValue === undefined || isNaN(numValue)) &&
              'required' in col
            ) {
              toast.error(t`${col.label || 'This field'} is required and cannot be empty`);
              clearEdit();
              return;
            }
            const result = col.onUpdate(context.row.original, {
              [col.id]: numValue,
            } as Partial<T>);
            clearEditOnSuccess(result, clearEdit);
          } else {
            clearEdit();
          }
        }}
      />
    </SharedEditableCell>
  );
};

// ============================================================================
// Field Type Configuration
// ============================================================================

/**
 * Number field type configuration
 */
export const numberFieldType: FieldTypeConfig = {
  placeholder: (label: string) => `Enter ${label.toLowerCase()}...`,
  ...S,
};

// ============================================================================
// Typed Renderer Exports (for registry)
// ============================================================================

/** Read-only renderer with proper typing */
export const numberReadOnly: ColumnRenderer = numberReadOnlyRenderer;

/** Editable renderer with proper typing */
export const numberEditable: ColumnRenderer = numberEditableRenderer;
